export const COLLECTION_VIEWS = [
  { key: 'grid', label: 'Grid', icon: 'ri-layout-grid-line' },
  { key: 'compact', label: 'Compact', icon: 'ri-apps-2-line' },
  { key: 'list', label: 'List', icon: 'ri-list-check' },
]

export const COLLECTION_SORTS = [
  { key: 'recent', label: 'Recent activity' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'name-asc', label: 'Name A–Z' },
  { key: 'name-desc', label: 'Name Z–A' },
]

const CONTROL_BASE =
  'border border-mk-line bg-mk-primary text-sm text-m1-ink transition-colors duration-200 hover:border-mk-line-2 hover:bg-mk-secondary focus:outline-none focus-visible:border-mk-line-3'

/**
 * Search, sort and view-mode controls for the My Cards collection, styled like a marketplace
 * toolbar: round 32px controls, a compact search field and a pill view switcher. The first
 * button shows or hides the filter rail. State lives in the parent so choices can be persisted.
 */
export default function CollectionToolbar({
  query,
  onQueryChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  filtersOpen,
  onToggleFilters,
  activeFilterCount,
  itemCount,
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleFilters}
          aria-pressed={filtersOpen}
          aria-label={filtersOpen ? 'Hide filters' : 'Show filters'}
          title={filtersOpen ? 'Hide filters' : 'Show filters'}
          className={`${CONTROL_BASE} relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-8 sm:w-8`}
        >
          <i
            className={`${filtersOpen ? 'ri-arrow-left-double-line' : 'ri-equalizer-line'} text-lg`}
            aria-hidden="true"
          />
          {activeFilterCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-m1-violet px-1 font-m1-mono text-[10px] leading-none text-m1-ground">
              {activeFilterCount}
            </span>
          )}
        </button>

        <label
          className={`${CONTROL_BASE} flex h-10 min-w-0 max-w-[400px] flex-1 cursor-text items-center gap-2 rounded-md pl-2.5 pr-3 focus-within:border-mk-line-3 sm:h-8`}
        >
          <span className="sr-only">Search your cards</span>
          <i className="ri-search-line text-lg text-mk-muted" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search by name or type"
            autoComplete="off"
            className="min-w-0 flex-1 !border-0 bg-transparent p-0 text-sm !font-normal text-m1-ink outline-none placeholder:text-mk-muted focus:ring-0 [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              aria-label="Clear search"
              className="flex h-6 w-6 shrink-0 items-center justify-center text-mk-muted transition-colors hover:text-m1-ink"
            >
              <i className="ri-close-line text-base" aria-hidden="true" />
            </button>
          )}
        </label>

        <div className="ml-auto hidden items-center gap-4 sm:flex">
          <SortSelect sort={sort} onSortChange={onSortChange} />
          <ViewToggle view={view} onViewChange={onViewChange} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 sm:hidden">
        <SortSelect sort={sort} onSortChange={onSortChange} className="min-w-0 flex-1" />
        <ViewToggle view={view} onViewChange={onViewChange} />
      </div>

      <p className="font-m1-mono text-xs uppercase leading-none text-mk-muted" role="status">
        <span className="font-m1-mono">{itemCount}</span> {itemCount === 1 ? 'item' : 'items'}
      </p>
    </div>
  )
}

function SortSelect({ sort, onSortChange, className = '' }) {
  return (
    <label className={`relative block ${className}`}>
      <span className="sr-only">Sort cards</span>
      <select
        value={sort}
        onChange={(event) => onSortChange(event.target.value)}
        className={`${CONTROL_BASE} h-10 w-full cursor-pointer appearance-none !rounded-full !border-mk-line !bg-mk-primary !font-normal hover:!border-mk-line-2 hover:!bg-mk-secondary focus:!border-mk-line-3 py-0 pl-3.5 pr-8 sm:h-8 sm:w-auto`}
      >
        {COLLECTION_SORTS.map((option) => (
          <option key={option.key} value={option.key} className="bg-mk-primary text-m1-ink">
            {option.label}
          </option>
        ))}
      </select>
      <i
        className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-lg text-mk-muted"
        aria-hidden="true"
      />
    </label>
  )
}

function ViewToggle({ view, onViewChange }) {
  return (
    <div
      role="group"
      aria-label="View mode"
      className="inline-flex shrink-0 self-start rounded-full"
    >
      {COLLECTION_VIEWS.map((option) => {
        const active = view === option.key
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onViewChange(option.key)}
            aria-pressed={active}
            aria-label={`${option.label} view`}
            title={`${option.label} view`}
            className={`relative flex aspect-square h-10 items-center justify-center rounded-full text-sm transition-[scale,colors] duration-200 ease-out focus:outline-none focus-visible:text-m1-ink active:scale-[0.97] sm:h-8 ${
              active ? 'font-medium text-m1-ink' : 'text-mk-muted hover:text-m1-ink'
            }`}
          >
            <i className={`${option.icon} relative z-[1] text-xl`} aria-hidden="true" />
            {active && (
              <span
                className="absolute inset-0 rounded-full border border-mk-line bg-mk-primary"
                aria-hidden="true"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}
